import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Edit3, Eye, EyeOff, Star, StarOff, ChevronUp, ChevronDown, Upload, X, Save, Image as ImageIcon, ExternalLink, Loader2, Library, Search } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

const EMPTY = {
  full_name: "", title: "", company: "", bio: "",
  topic: "", session_title: "", speaking_time: "",
  photo_url: "", website_url: "", facebook_url: "",
  instagram_url: "", linkedin_url: "",
  language: "vi", is_featured: false, is_visible: true,
};

export default function SpeakersManager({ slug }) {
  const { lang, t } = useI18n();
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode:'create'|'edit', data}
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState("");
  const [picker, setPicker] = useState(false);

  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const headers = { "X-Admin-Token": token };

  const VI = lang !== "en";
  const L = (vi, en) => (VI ? vi : en);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2000); };

  const attachFromLibrary = async (speaker_id) => {
    try {
      await api.post(`/admin/nail-events/${slug}/event-speakers`, { speaker_id }, { headers });
      setPicker(false);
      flash(L("Đã gắn", "Attached"));
      load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/nail-events/${slug}/speakers`, { headers });
      setSpeakers(r.data.speakers || []);
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const saveSpeaker = async (payload, id) => {
    try {
      if (id) {
        await api.put(`/admin/nail-events/${slug}/speakers/${id}`, payload, { headers });
      } else {
        await api.post(`/admin/nail-events/${slug}/speakers`, payload, { headers });
      }
      setEditing(null);
      flash(L("Đã lưu", "Saved"));
      load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    }
  };

  const togglePatch = async (sp, patch) => {
    try {
      await api.put(`/admin/nail-events/${slug}/speakers/${sp.id}`, patch, { headers });
      load();
    } catch (err) {
      flash(err?.response?.data?.detail || "Error");
    }
  };

  const move = async (idx, dir) => {
    const next = [...speakers];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setSpeakers(next);
    try {
      await api.post(`/admin/nail-events/${slug}/speakers/reorder`, { order: next.map((s) => s.id) }, { headers });
    } catch (err) { flash(err?.response?.data?.detail || "Error"); load(); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/nail-events/${slug}/speakers/${id}`, { headers });
      setConfirmDelete(null);
      flash(L("Đã xóa", "Deleted"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="speakers-manager">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-charcoal">{L("Diễn Giả", "Speakers")}</h2>
          <p className="text-sm text-warmgray-700 mt-1">{L("Quản lý diễn giả cho sự kiện này", "Manage speakers for this event")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setPicker(true)}
            data-testid="speaker-pick-library-btn"
            className="inline-flex items-center gap-1.5 border border-charcoal text-charcoal rounded-full px-4 py-2 text-sm font-medium hover:bg-warmgray-100 transition-all">
            <Library className="w-4 h-4" /> {L("Chọn Từ Thư Viện", "Pick from Library")}
          </button>
          <button type="button" onClick={() => setEditing({ mode: "create", data: { ...EMPTY, language: lang } })}
            data-testid="speaker-add-btn"
            className="inline-flex items-center gap-1.5 bg-charcoal text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-charcoal-700 transition-all">
            <Plus className="w-4 h-4" /> {L("Thêm Diễn Giả", "Add Speaker")}
          </button>
        </div>
      </div>

      {toast && <div data-testid="speakers-toast" className="inline-block bg-emerald2/10 text-emerald2 rounded-xl px-4 py-2 text-sm font-medium mb-4">{toast}</div>}

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-warmgray-500" /></div>
      ) : speakers.length === 0 ? (
        <div className="bg-warmgray-50 border border-dashed border-warmgray-300 rounded-3xl p-10 text-center">
          <ImageIcon className="w-6 h-6 text-warmgray-500 mx-auto" />
          <p className="mt-3 text-warmgray-700">{L("Chưa có diễn giả. Bấm “Thêm Diễn Giả” để bắt đầu.", "No speakers yet. Click “Add Speaker” to begin.")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {speakers.map((sp, i) => (
            <SpeakerRow key={sp.id} sp={sp} i={i} total={speakers.length}
              onMove={(dir) => move(i, dir)}
              onEdit={() => setEditing({ mode: "edit", data: sp })}
              onToggleVisible={() => togglePatch(sp, { is_visible: !sp.is_visible })}
              onToggleFeatured={() => togglePatch(sp, { is_featured: !sp.is_featured })}
              onDelete={() => setConfirmDelete(sp)}
              L={L} />
          ))}
        </div>
      )}

      {editing && (
        <SpeakerDialog
          mode={editing.mode}
          initial={editing.data}
          onClose={() => setEditing(null)}
          onSave={(payload) => saveSpeaker(payload, editing.mode === "edit" ? editing.data.id : null)}
          L={L}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={L("Xóa diễn giả?", "Delete speaker?")}
          body={L(`Bạn có chắc muốn xóa “${confirmDelete.full_name}”? Hành động này không thể hoàn tác.`, `Are you sure you want to delete “${confirmDelete.full_name}”? This cannot be undone.`)}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove(confirmDelete.id)}
          L={L}
        />
      )}

      {picker && (
        <SpeakerLibraryPicker
          existing={speakers.map((s) => s.speaker_id).filter(Boolean)}
          headers={headers}
          onClose={() => setPicker(false)}
          onPick={attachFromLibrary}
          L={L}
        />
      )}
    </div>
  );
}

function SpeakerLibraryPicker({ existing, headers, onClose, onPick, L }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/speakers", { headers, params: q ? { q } : {} });
      setItems(r.data.speakers || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="speaker-picker">
      <div className="w-full max-w-2xl bg-white rounded-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl text-charcoal">{L("Chọn Diễn Giả Từ Thư Viện", "Pick Speaker From Library")}</h3>
          <button type="button" onClick={onClose} data-testid="speaker-picker-close" className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="px-6 pt-4 flex items-center gap-3 border-b border-warmgray-100 pb-4">
          <Search className="w-4 h-4 text-warmgray-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Tìm theo tên...", "Search by name...")} data-testid="speaker-picker-search"
            className="flex-1 bg-transparent text-sm focus:outline-none" />
        </form>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-warmgray-500" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-warmgray-700">{L("Không có diễn giả. Tạo mới từ Thư Viện trước.", "No speakers. Create one in the Library first.")}</div>
          ) : (
            <div className="space-y-2">
              {items.map((sp) => {
                const already = existing.includes(sp.id);
                return (
                  <button key={sp.id} type="button" disabled={already} onClick={() => onPick(sp.id)} data-testid={`speaker-pick-${sp.id}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-2xl border ${already ? "border-warmgray-200 bg-warmgray-50 opacity-60" : "border-warmgray-200 hover:border-emerald2 hover:bg-emerald2/5"}`}>
                    <div className="w-12 h-12 rounded-xl bg-warmgray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                      {sp.photo_url ? <img src={sp.photo_url} alt={sp.full_name} className="w-full h-full object-cover" /> : <span className="font-serif text-xl text-warmgray-500">{sp.full_name?.[0]}</span>}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="font-medium text-charcoal truncate">{sp.full_name}</div>
                      <div className="text-xs text-warmgray-700 truncate">{[sp.title, sp.company].filter(Boolean).join(" · ")}</div>
                    </div>
                    {already && <span className="text-[10px] uppercase tracking-[0.18em] text-warmgray-500">{L("Đã gắn", "Attached")}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SpeakerRow({ sp, i, total, onMove, onEdit, onToggleVisible, onToggleFeatured, onDelete, L }) {
  return (
    <div data-testid={`speaker-row-${sp.id}`}
      className={`bg-white rounded-2xl p-4 flex items-center gap-4 border ${sp.is_featured ? "border-gold-500/60 shadow-[0_18px_40px_-20px_rgba(201,161,74,0.4)]" : "border-warmgray-200"}`}>
      <div className="flex flex-col gap-1">
        <button type="button" onClick={() => onMove(-1)} disabled={i === 0} data-testid={`speaker-up-${sp.id}`}
          className="text-warmgray-500 hover:text-charcoal disabled:opacity-30 p-1"><ChevronUp className="w-4 h-4" /></button>
        <button type="button" onClick={() => onMove(1)} disabled={i === total - 1} data-testid={`speaker-down-${sp.id}`}
          className="text-warmgray-500 hover:text-charcoal disabled:opacity-30 p-1"><ChevronDown className="w-4 h-4" /></button>
      </div>
      <div className="w-14 h-14 rounded-2xl bg-warmgray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
        {sp.photo_url ? (
          <img src={sp.photo_url} alt={sp.full_name} className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-5 h-5 text-warmgray-500" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-charcoal truncate">{sp.full_name}</span>
          {sp.is_featured && <span className="inline-flex items-center gap-1 bg-gold-500 text-charcoal rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"><Star className="w-2.5 h-2.5" /> {L("Nổi Bật", "Featured")}</span>}
          {!sp.is_visible && <span className="bg-warmgray-200 text-warmgray-700 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{L("Đã Ẩn", "Hidden")}</span>}
        </div>
        <div className="text-sm text-warmgray-700 truncate">{[sp.title, sp.company].filter(Boolean).join(" · ") || sp.topic || ""}</div>
        {sp.speaking_time && <div className="text-xs text-emerald2 mt-0.5 font-medium">{sp.speaking_time}</div>}
      </div>
      <div className="flex items-center gap-1">
        <IconBtn title={sp.is_visible ? L("Ẩn", "Hide") : L("Hiện", "Show")} onClick={onToggleVisible} testId={`speaker-toggle-visible-${sp.id}`}>
          {sp.is_visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4 text-warmgray-500" />}
        </IconBtn>
        <IconBtn title={sp.is_featured ? L("Bỏ nổi bật", "Unfeature") : L("Đánh dấu nổi bật", "Mark featured")} onClick={onToggleFeatured} testId={`speaker-toggle-featured-${sp.id}`}>
          {sp.is_featured ? <StarOff className="w-4 h-4 text-gold-500" /> : <Star className="w-4 h-4" />}
        </IconBtn>
        <IconBtn title={L("Chỉnh sửa", "Edit")} onClick={onEdit} testId={`speaker-edit-${sp.id}`}>
          <Edit3 className="w-4 h-4" />
        </IconBtn>
        <IconBtn title={L("Xóa", "Delete")} onClick={onDelete} testId={`speaker-delete-${sp.id}`} danger>
          <Trash2 className="w-4 h-4" />
        </IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, title, testId, danger }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testId}
      className={`p-2 rounded-lg transition-all ${danger ? "text-warmgray-500 hover:text-[#7A2E2E] hover:bg-[#F5DADA]" : "text-warmgray-700 hover:text-charcoal hover:bg-warmgray-100"}`}>
      {children}
    </button>
  );
}

function SpeakerDialog({ mode, initial, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [imgValid, setImgValid] = useState(true);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validUrl = (u) => !u || /^https?:\/\//i.test(u);
  const canSave = form.full_name.trim().length > 0 && validUrl(form.photo_url) && validUrl(form.website_url);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setSaving(true);
    await onSave({
      full_name: form.full_name.trim(),
      title: form.title || null,
      company: form.company || null,
      bio: form.bio || null,
      topic: form.topic || null,
      session_title: form.session_title || null,
      speaking_time: form.speaking_time || null,
      photo_url: form.photo_url || null,
      website_url: form.website_url || null,
      facebook_url: form.facebook_url || null,
      instagram_url: form.instagram_url || null,
      linkedin_url: form.linkedin_url || null,
      language: form.language || "vi",
      is_featured: !!form.is_featured,
      is_visible: form.is_visible !== false,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="speaker-dialog">
      <form onSubmit={submit} className="w-full max-w-3xl bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] max-h-[92vh] flex flex-col">
        <div className="px-6 md:px-8 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal">
            {mode === "edit" ? L("Chỉnh sửa diễn giả", "Edit speaker") : L("Thêm Diễn Giả", "Add Speaker")}
          </h3>
          <button type="button" onClick={onClose} data-testid="speaker-dialog-close" className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-6 md:px-8 py-6 overflow-y-auto grid md:grid-cols-3 gap-6">
          {/* Photo column */}
          <div className="md:col-span-1">
            <Label>{L("Ảnh Diễn Giả", "Speaker photo")}</Label>
            <div className="mt-2 aspect-square rounded-2xl bg-warmgray-100 overflow-hidden flex items-center justify-center border border-warmgray-200">
              {form.photo_url && imgValid ? (
                <img src={form.photo_url} alt="" onError={() => setImgValid(false)} className="w-full h-full object-cover" />
              ) : (
                <div className="text-center text-warmgray-500 px-4">
                  <ImageIcon className="w-6 h-6 mx-auto" />
                  <p className="text-xs mt-2">{L("Xem trước sẽ hiện ở đây", "Preview will appear here")}</p>
                </div>
              )}
            </div>
            <button type="button" disabled title={L("Upload sắp ra mắt — dùng URL", "Upload coming soon — use URL")}
              data-testid="speaker-upload-disabled"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-warmgray-100 text-warmgray-700 rounded-full px-3 py-2 text-xs font-medium cursor-not-allowed">
              <Upload className="w-3.5 h-3.5" /> {L("Upload (sắp ra mắt)", "Upload (coming soon)")}
            </button>
            <Field label={L("URL ảnh", "Photo URL")} value={form.photo_url} onChange={(v) => { set("photo_url", v); setImgValid(true); }}
              placeholder="https://..." testId="speaker-photo-url" />
            <p className="text-[10px] text-warmgray-500 mt-1 leading-relaxed">{L("Khuyến nghị tỉ lệ 1:1 vuông hoặc 4:5 dọc", "Recommended 1:1 square or 4:5 portrait")}</p>
          </div>

          {/* Details column */}
          <div className="md:col-span-2 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label={L("Tên Diễn Giả", "Full name") + " *"} value={form.full_name} onChange={(v) => set("full_name", v)} testId="speaker-full-name" required />
              <Field label={L("Thời Gian Trình Bày", "Speaking time")} value={form.speaking_time} onChange={(v) => set("speaking_time", v)} placeholder="7:30 PM" testId="speaker-time" />
              <Field label={L("Chức Danh", "Title")} value={form.title} onChange={(v) => set("title", v)} testId="speaker-title" />
              <Field label={L("Công Ty", "Company")} value={form.company} onChange={(v) => set("company", v)} testId="speaker-company" />
              <Field label={L("Chủ Đề", "Topic")} value={form.topic} onChange={(v) => set("topic", v)} testId="speaker-topic" />
              <Field label={L("Tiêu Đề Buổi Nói Chuyện", "Session title")} value={form.session_title} onChange={(v) => set("session_title", v)} testId="speaker-session" />
            </div>
            <Textarea label={L("Tiểu Sử", "Bio")} value={form.bio} onChange={(v) => set("bio", v)} rows={3} testId="speaker-bio" />

            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Website" value={form.website_url} onChange={(v) => set("website_url", v)} placeholder="https://..." testId="speaker-website" />
              <Field label="LinkedIn" value={form.linkedin_url} onChange={(v) => set("linkedin_url", v)} placeholder="https://linkedin.com/..." testId="speaker-linkedin" />
              <Field label="Facebook" value={form.facebook_url} onChange={(v) => set("facebook_url", v)} placeholder="https://facebook.com/..." testId="speaker-facebook" />
              <Field label="Instagram" value={form.instagram_url} onChange={(v) => set("instagram_url", v)} placeholder="https://instagram.com/..." testId="speaker-instagram" />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
              <Toggle label={L("Hiển Thị", "Visible")} value={form.is_visible !== false} onChange={(v) => set("is_visible", v)} testId="speaker-visible" />
              <Toggle label={L("Nổi Bật", "Featured")} value={!!form.is_featured} onChange={(v) => set("is_featured", v)} testId="speaker-featured" />
              <label className="flex items-center justify-between bg-warmgray-50 border border-warmgray-200 rounded-xl px-3 py-2.5">
                <span className="text-xs font-medium text-warmgray-700">{L("Ngôn ngữ", "Language")}</span>
                <select value={form.language || "vi"} onChange={(e) => set("language", e.target.value)} data-testid="speaker-language"
                  className="bg-transparent text-sm focus:outline-none">
                  <option value="vi">VI</option>
                  <option value="en">EN</option>
                </select>
              </label>
            </div>
          </div>
        </div>

        <div className="px-6 md:px-8 py-4 border-t border-warmgray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid="speaker-dialog-cancel">
            {L("Hủy", "Cancel")}
          </button>
          <button type="submit" disabled={!canSave || saving} data-testid="speaker-dialog-save"
            className="inline-flex items-center gap-2 bg-emerald2 text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-emerald2-600 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {L("Lưu", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmDialog({ title, body, onCancel, onConfirm, L }) {
  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/40 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)]" data-testid="confirm-dialog">
        <h3 className="font-serif text-2xl text-charcoal">{title}</h3>
        <p className="mt-2 text-sm text-warmgray-700">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal">{L("Hủy", "Cancel")}</button>
          <button type="button" onClick={onConfirm} data-testid="confirm-confirm" className="bg-[#7A2E2E] text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90">{L("Xóa", "Delete")}</button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700">{children}</span>;
}

function Field({ label, value, onChange, placeholder, testId, required }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}

function Textarea({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}

function Toggle({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between bg-warmgray-50 border border-warmgray-200 rounded-xl px-3 py-2.5 cursor-pointer">
      <span className="text-xs font-medium text-warmgray-700">{label}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="rounded" />
    </label>
  );
}
